import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Label, LabelsByBoards } from '../../../../../../../common/labels/labelTypes';
import { fetchBoards } from '../../../../../../element/board/boardService';
import {
    selectBoardsToFetchForCurrentPath,
    selectCurrentPathLabels,
} from '../../../../../../element/labels/labelSelectors';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import { getCurrentUserId } from '../../../../../../user/currentUserSelector';
import { mergeCurrentPathLabels, mergeSuggestedLabels } from '../utils/labelListMergeUtils';
import { createRecentLabelsSection, translateRootBoardTitle, RECENT_LABELS_SECTION_ID } from '../utils/labelPopupUtils';

type Props = {
    allLabels: Label[];
    searchString: string;
};

type UseLabelListDataReturnType = {
    allLabelsFiltered: Label[];
    suggestedLabels: LabelsByBoards;
    hasVisibleLabels: boolean;
    isLoadingSuggestedLabels: boolean;
    isSearching: boolean;
};

type SuggestedLabelSource = 'current-path' | 'recent' | 'combined' | 'loading';

type SuggestedLabelState = {
    labels: LabelsByBoards;
    source: SuggestedLabelSource;
} | null;

const useLabelListData = ({ allLabels, searchString }: Props): UseLabelListDataReturnType => {
    const dispatch = useDispatch();
    const { toFetch, isBoardFetchInProgress } = useSelector(selectBoardsToFetchForCurrentPath);
    const currentPathLabels = useSelector(selectCurrentPathLabels);
    const currentUserId = useSelector(getCurrentUserId);
    const translate = useTranslator('popup', 'labels.menu');
    const appHeaderTranslate = useTranslator('appHeader');
    const recentlyCreatedTitle = translate('recentlyCreated');
    const translatedHomeTitle = appHeaderTranslate('breadcrumbs.home');
    const translatedCurrentPathLabels = useMemo(
        () => translateRootBoardTitle(currentPathLabels, translatedHomeTitle),
        [currentPathLabels, translatedHomeTitle],
    );

    const isCurrentPathResolving = isBoardFetchInProgress || toFetch.length > 0;
    const [suggestedLabelsState, setSuggestedLabelsState] = useState<SuggestedLabelState>(() => {
        const recentLabels = createRecentLabelsSection(allLabels, currentUserId, recentlyCreatedTitle);

        if (translatedCurrentPathLabels.length) {
            return {
                labels: translatedCurrentPathLabels,
                source: 'current-path',
            };
        }

        if (isCurrentPathResolving && recentLabels.length) {
            return {
                labels: recentLabels,
                source: 'loading',
            };
        }

        return null;
    });

    const normalizedSearch = searchString.trim().toLowerCase();
    const isSearching = Boolean(normalizedSearch);

    useEffect(() => {
        if (!toFetch.length) return;

        dispatch(fetchBoards({ boardIds: toFetch }));
    }, [dispatch, toFetch]);

    useLayoutEffect(() => {
        if (isCurrentPathResolving) return;

        setSuggestedLabelsState((prevState) => {
            if (!translatedCurrentPathLabels.length) {
                const recentLabels = createRecentLabelsSection(allLabels, currentUserId, recentlyCreatedTitle);
                return {
                    labels: recentLabels.length ? recentLabels : [],
                    source: 'recent',
                };
            }

            if (prevState?.source === 'recent' || prevState?.source === 'combined') {
                const mergedLabels = mergeSuggestedLabels(prevState.labels, translatedCurrentPathLabels, allLabels);
                const stillHasRecentSection = mergedLabels.some(({ id }) => id === RECENT_LABELS_SECTION_ID);

                return {
                    labels: mergedLabels.length ? mergedLabels : translatedCurrentPathLabels,
                    source: stillHasRecentSection ? 'combined' : 'current-path',
                };
            }

            if (prevState?.source === 'current-path') {
                const mergedCurrentPathLabels = mergeCurrentPathLabels(
                    prevState.labels,
                    translatedCurrentPathLabels,
                    allLabels,
                );

                return {
                    labels: mergedCurrentPathLabels,
                    source: 'current-path',
                };
            }

            return {
                labels: translatedCurrentPathLabels,
                source: 'current-path',
            };
        });
    }, [allLabels, translatedCurrentPathLabels, currentUserId, isCurrentPathResolving, recentlyCreatedTitle]);

    const allLabelsFiltered = useMemo(() => {
        return allLabels.filter((label) => !isSearching || label.name.toLowerCase().includes(normalizedSearch));
    }, [allLabels, isSearching, normalizedSearch]);

    const suggestedLabels = suggestedLabelsState?.labels ?? [];

    const isLoadingSuggestedLabels = isCurrentPathResolving && !isSearching && !suggestedLabels.length;
    const hasVisibleLabels = isSearching ? allLabelsFiltered.length > 0 : suggestedLabels.length > 0;

    return {
        allLabelsFiltered,
        suggestedLabels,
        hasVisibleLabels,
        isLoadingSuggestedLabels,
        isSearching,
    };
};

export default useLabelListData;
